import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { SearchModal } from "../components/search/SearchModal";
import { TableView } from "../components/table/TableView";
import { Button } from "../components/ui/Button";
import { SEAT_COUNT } from "../constants";
import { useAuth } from "../context/AuthContext";
import { useParty } from "../hooks/useParty";
import { apiClient } from "../lib/api";
import type { Celebrity, Party } from "../lib/types";

type AutoSaveStatus = "idle" | "saving" | "saved";

export function Build() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug?: string }>();
  const { user, refetch: refetchUser } = useAuth();
  const { party: existingParty, isLoading: partyLoading } = useParty(slug);
  const navigate = useNavigate();
  const isEditMode = Boolean(slug);

  const [title, setTitle] = useState("");
  const [seats, setSeats] = useState<(Celebrity | null)[]>(
    Array(SEAT_COUNT).fill(null),
  );
  const [activeSeat, setActiveSeat] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialised, setInitialised] = useState(!isEditMode);
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>("idle");
  const [partyLimitChecked, setPartyLimitChecked] = useState(isEditMode);
  const titleSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Redirect to home if user has already hit the 5-party limit (create mode only)
  useEffect(() => {
    if (isEditMode || !user) return;
    apiClient.get<Party[]>("/api/parties").then((parties) => {
      if (parties.length >= 5) navigate("/");
      else setPartyLimitChecked(true);
    });
  }, [isEditMode, user, navigate]);

  // Populate form from existing party in edit mode
  useEffect(() => {
    if (existingParty && !initialised) {
      setTitle(existingParty.title);
      const loaded: (Celebrity | null)[] = Array(SEAT_COUNT).fill(null);
      for (const guest of existingParty.guests) {
        if (guest.seat_order >= 0 && guest.seat_order < SEAT_COUNT) {
          loaded[guest.seat_order] = guest.celebrity;
        }
      }
      setSeats(loaded);
      setInitialised(true);
    }
  }, [existingParty, initialised]);

  // Autosave title after 800ms of inactivity (edit mode only)
  useEffect(() => {
    if (!isEditMode || !slug || !initialised || !title.trim()) return;
    if (titleSaveTimer.current) clearTimeout(titleSaveTimer.current);
    titleSaveTimer.current = setTimeout(async () => {
      setAutoSaveStatus("saving");
      try {
        await apiClient.patch(`/api/parties/${slug}`, { title: title.trim() });
        setAutoSaveStatus("saved");
        setTimeout(() => setAutoSaveStatus("idle"), 2000);
      } catch {
        setAutoSaveStatus("idle");
      }
    }, 800);
    return () => {
      if (titleSaveTimer.current) clearTimeout(titleSaveTimer.current);
    };
  }, [title, isEditMode, slug, initialised]);

  const filledCount = seats.filter(Boolean).length;
  const canSaveDraft = title.trim().length > 0 && !isSubmitting;
  const canPublish =
    title.trim().length > 0 && filledCount === SEAT_COUNT && !isSubmitting;

  function buildGuestPayload(currentSeats: (Celebrity | null)[]) {
    return currentSeats
      .map((celebrity, index) =>
        celebrity
          ? {
              external_id: celebrity.external_id ?? String(celebrity.id),
              source: celebrity.source,
              name: celebrity.name,
              image_url: celebrity.image_url,
              known_for: celebrity.known_for,
              seat_order: index,
            }
          : null,
      )
      .filter(Boolean);
  }

  async function autoSave(currentSeats: (Celebrity | null)[]) {
    if (!slug) return;
    setAutoSaveStatus("saving");
    try {
      await apiClient.patch(`/api/parties/${slug}`, {
        guests: buildGuestPayload(currentSeats),
      });
      setAutoSaveStatus("saved");
      setTimeout(() => setAutoSaveStatus("idle"), 2000);
    } catch {
      setAutoSaveStatus("idle");
    }
  }

  function updateSeat(index: number, celebrity: Celebrity | null) {
    if (celebrity) {
      const isDuplicate = seats.some(
        (s, i) =>
          i !== index &&
          s?.external_id === celebrity.external_id &&
          s?.source === celebrity.source,
      );
      if (isDuplicate) {
        toast.error(t("party.duplicateGuest"));
        return;
      }
    }
    const next = [...seats];
    next[index] = celebrity;
    setSeats(next);
    if (isEditMode) autoSave(next);
  }

  async function handleDelete() {
    if (!slug) return;
    setIsDeleting(true);
    try {
      await apiClient.delete(`/api/parties/${slug}`);
      refetchUser();
      navigate("/");
    } catch {
      setIsDeleting(false);
      setConfirmDelete(false);
    }
  }

  async function handleSave(status: "draft" | "published") {
    setIsSubmitting(true);
    setError(null);
    try {
      if (isEditMode && slug) {
        await apiClient.patch<Party>(`/api/parties/${slug}`, {
          title: title.trim(),
          status,
          guests: buildGuestPayload(seats),
        });
        if (status === "published") refetchUser();
        navigate(`/party/${slug}`);
      } else {
        const created = await apiClient.post<Party>("/api/parties", {
          title: title.trim(),
        });
        const updated = await apiClient.patch<Party>(
          `/api/parties/${created.slug}`,
          {
            status,
            guests: buildGuestPayload(seats),
          },
        );
        if (status === "published") refetchUser();
        navigate(`/party/${updated.slug}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errors.partyNotFound"));
    } finally {
      setIsSubmitting(false);
    }
  }

  const isLoading =
    (isEditMode && partyLoading && !initialised) || !partyLimitChecked;

  if (isLoading) {
    return (
      <main className="min-h-screen bg-stone-50 dark:bg-stone-950 text-stone-900 dark:text-stone-100 pt-14 flex items-center justify-center">
        <div className="w-5 h-5 rounded-full border-2 border-stone-300 dark:border-stone-700 border-t-amber-500 animate-spin" />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-stone-50 dark:bg-stone-950 text-stone-900 dark:text-stone-100 pt-14">
      <div className="max-w-2xl mx-auto px-4 py-12 flex flex-col gap-8 pb-32">
        <div>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 transition-colors text-sm mb-4"
          >
            {t("nav.back")}
          </Link>
          <h1 className="text-3xl font-bold text-stone-900 dark:text-stone-100 mb-6">
            {isEditMode ? t("party.editHeading") : t("party.buildHeading")}
          </h1>
          <div className="relative max-w-sm">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("party.namePlaceholder")}
              className="text-2xl bg-transparent border-b-2 border-stone-300 dark:border-stone-700 focus:border-amber-500 outline-none text-stone-900 dark:text-stone-100 pb-2 w-full placeholder:text-stone-400 dark:placeholder:text-stone-600 transition-all duration-200"
            />
          </div>
        </div>

        <TableView
          seats={seats}
          readonly={false}
          onAdd={(i) => setActiveSeat(i)}
          onRemove={(i) => updateSeat(i, null)}
        />

        <p className="text-stone-400 dark:text-stone-600 text-sm text-center">
          {t("party.addGuestInstruction")}
        </p>

        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-1.5">
            {Array.from({ length: SEAT_COUNT }).map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                  i < filledCount
                    ? "bg-amber-500"
                    : "bg-stone-200 dark:bg-stone-800"
                }`}
              />
            ))}
          </div>
          <p className="text-stone-500 dark:text-stone-400 text-sm">
            {t("seats.filled", { count: filledCount, total: SEAT_COUNT })}
            {filledCount < SEAT_COUNT && (
              <span className="text-stone-400 dark:text-stone-600">
                {" "}
                {t("party.fillAllSeats")}
              </span>
            )}
          </p>
        </div>

        {error && (
          <p className="text-red-500 dark:text-red-400 text-sm text-center">
            {error}
          </p>
        )}
      </div>

      <div className="sticky bottom-0 bg-stone-50/90 dark:bg-stone-950/90 backdrop-blur border-t border-stone-200 dark:border-stone-800 py-4 px-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          {isEditMode && existingParty?.status === "draft" && (
            <div className="flex items-center gap-2 mr-auto">
              {confirmDelete ? (
                <>
                  <span className="text-stone-500 dark:text-stone-400 text-sm">
                    {t("confirmation.areYouSure")}
                  </span>
                  <Button
                    variant="ghost"
                    onClick={() => setConfirmDelete(false)}
                    disabled={isDeleting}
                  >
                    {t("confirmation.cancel")}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={handleDelete}
                    disabled={isDeleting}
                  >
                    <span className="text-red-500 dark:text-red-400">
                      {isDeleting ? t("party.deleting") : t("party.delete")}
                    </span>
                  </Button>
                </>
              ) : (
                <Button variant="ghost" onClick={() => setConfirmDelete(true)}>
                  <span className="text-stone-400">{t("party.delete")}</span>
                </Button>
              )}
            </div>
          )}

          {isEditMode && autoSaveStatus !== "idle" && (
            <span className="text-xs text-stone-400 dark:text-stone-600 ml-auto mr-2">
              {autoSaveStatus === "saving"
                ? t("party.autosaveSaving")
                : t("party.autosaveSaved")}
            </span>
          )}

          {!isEditMode && (
            <Button
              variant="secondary"
              onClick={() => handleSave("draft")}
              disabled={!canSaveDraft}
              className="ml-auto"
            >
              {isSubmitting ? t("party.saving") : t("party.saveDraft")}
            </Button>
          )}

          <Button
            variant="primary"
            onClick={() => handleSave("published")}
            disabled={!canPublish}
            title={
              filledCount < SEAT_COUNT ? t("party.publishTooltip") : undefined
            }
          >
            {isSubmitting ? t("party.publishing") : t("party.publish")}
          </Button>
        </div>
      </div>

      <SearchModal
        open={activeSeat !== null}
        onClose={() => setActiveSeat(null)}
        onSelect={(celebrity) => {
          if (activeSeat === null) return;
          updateSeat(activeSeat, celebrity);
          setActiveSeat(null);
        }}
      />
    </main>
  );
}
